/** Canonical track-local language metadata shared by audio and subtitles. */
const ISO2_TO_3: Record<string, string> = Object.fromEntries(
  ('aa:aar ab:abk ae:ave af:afr ak:aka am:amh an:arg ar:ara as:asm av:ava ay:aym az:aze ' +
   'ba:bak be:bel bg:bul bh:bih bi:bis bm:bam bn:ben bo:bod br:bre bs:bos ca:cat ce:che ch:cha co:cos cr:cre cs:ces cu:chu cv:chv cy:cym ' +
   'da:dan de:deu dv:div dz:dzo ee:ewe el:ell en:eng eo:epo es:spa et:est eu:eus fa:fas ff:ful fi:fin fj:fij fo:fao fr:fra fy:fry ' +
   'ga:gle gd:gla gl:glg gn:grn gu:guj gv:glv ha:hau he:heb hi:hin ho:hmo hr:hrv ht:hat hu:hun hy:hye hz:her ' +
   'ia:ina id:ind ie:ile ig:ibo ii:iii ik:ipk io:ido is:isl it:ita iu:iku ja:jpn jv:jav ka:kat kg:kon ki:kik kj:kua kk:kaz kl:kal km:khm kn:kan ko:kor kr:kau ks:kas ku:kur kv:kom kw:cor ky:kir ' +
   'la:lat lb:ltz lg:lug li:lim ln:lin lo:lao lt:lit lu:lub lv:lav mg:mlg mh:mah mi:mri mk:mkd ml:mal mn:mon mr:mar ms:msa mt:mlt my:mya ' +
   'na:nau nb:nob nd:nde ne:nep ng:ndo nl:nld nn:nno no:nor nr:nbl nv:nav ny:nya oc:oci oj:oji om:orm or:ori os:oss ' +
   'pa:pan pi:pli pl:pol ps:pus pt:por qu:que rm:roh rn:run ro:ron ru:rus rw:kin sa:san sc:srd sd:snd se:sme sg:sag si:sin sk:slk sl:slv sm:smo sn:sna so:som sq:sqi sr:srp ss:ssw st:sot su:sun sv:swe sw:swa ' +
   'ta:tam te:tel tg:tgk th:tha ti:tir tk:tuk tl:tgl tn:tsn to:ton tr:tur ts:tso tt:tat tw:twi ty:tah ug:uig uk:ukr ur:urd uz:uzb ve:ven vi:vie vo:vol wa:wln wo:wol xh:xho yi:yid yo:yor za:zha zh:zho zu:zul')
    .split(' ').map(pair => pair.split(':'))
);

const TITLE_NAMES: Record<string, string> = {
  english: 'en', french: 'fr', français: 'fr', german: 'de', deutsch: 'de',
  spanish: 'es', español: 'es', italian: 'it', italiano: 'it', dutch: 'nl',
  portuguese: 'pt', português: 'pt', russian: 'ru', japanese: 'ja', korean: 'ko',
  chinese: 'zh', mandarin: 'zh', cantonese: 'yue', arabic: 'ar', hindi: 'hi',
  polish: 'pl', swedish: 'sv', norwegian: 'no', danish: 'da', finnish: 'fi',
  turkish: 'tr', greek: 'el', hebrew: 'he', czech: 'cs', hungarian: 'hu',
  romanian: 'ro', thai: 'th', vietnamese: 'vi', indonesian: 'id', ukrainian: 'uk',
  tamil: 'ta', telugu: 'te', malayalam: 'ml', bengali: 'bn', persian: 'fa'
};

/** ISO 639 B/T aliases become BCP47; region/script subtags are retained. */
export function normalizeTrackLanguage(value?: string | null): string {
  const raw = (value ?? '').replace(/\0.*$/s, '').trim().replace(/_/g, '-');
  // Long words such as "unknown" are syntactically legal BCP47 primaries but
  // are not language codes. Private-use and und-* do not identify a language.
  if (!/^[a-z]{2,3}(?:-[a-z0-9]{1,8})*$/i.test(raw)) return 'und';
  try {
    const tag = Intl.getCanonicalLocales(raw)[0]!;
    return /^(und|mis)(?:-|$)/i.test(tag) ? 'und' : tag;
  } catch {
    return 'und';
  }
}

/** Short shared API for labels and persisted preference normalization. */
export const normalizeLanguage = normalizeTrackLanguage;

/** Match language preferences across ISO aliases, ignoring region/script.
 * Unknown, multilingual and non-linguistic tracks cannot satisfy a language.
 * For exact regional/script identity, compare normalizeLanguage results. */
export function sameTrackLanguage(a?: string | null, b?: string | null): boolean {
  const left = normalizeTrackLanguage(a).split('-')[0]!;
  const right = normalizeTrackLanguage(b).split('-')[0]!;
  return !['und', 'mul', 'zxx'].includes(left) && left === right;
}

/** mdhd stores only an ISO 639-2 primary, never a BCP47 region/script. */
export function trackLanguageIso3(value?: string | null): string {
  const base = normalizeTrackLanguage(value).split('-')[0]!;
  return ISO2_TO_3[base] ?? (base.length === 3 ? base : 'und');
}

function titleLanguage(title?: string | null): string {
  if (!title) return 'und';
  const text = title.toLowerCase().trim();
  const candidates = new Set<string>();
  // Full language names must start a label or a bracketed/delimited component.
  // Multiple named languages are ambiguous rather than evidence for the first.
  for (const [name, lang] of Object.entries(TITLE_NAMES)) {
    if (new RegExp(`(?:^|[\\[(/|;,+])\\s*${name}(?=$|[^\\p{L}])`, 'u').test(text)) candidates.add(lang);
  }
  // Three-letter ISO labels such as "ENG VO : AC3" are common track names.
  // Require a known language and a technical delimiter, not an arbitrary word.
  const prefix = /^(?:\[)?([a-z]{3})(?=\s+(?:sdh|cc|vo|forced|full|aac|ac3|dts|truehd|[257]\.1)\b|\s*[:|/])/i.exec(text);
  if (prefix) {
    const code = normalizeTrackLanguage(prefix[1]);
    if (Object.values(ISO2_TO_3).includes(prefix[1]!) && code !== 'und') candidates.add(code);
  }
  // Short codes only count as the entire label or in brackets. This avoids
  // interpreting ordinary words ("it", "hi", "no") as language evidence.
  for (const label of [text, ...Array.from(text.matchAll(/\[([^\]]+)\]|\(([^)]+)\)/g), m => m[1] ?? m[2]!)]) {
    const lang = normalizeTrackLanguage(label);
    const base = lang.split('-')[0]!;
    if (ISO2_TO_3[base] || Object.values(TITLE_NAMES).includes(base)) candidates.add(lang);
  }
  const tags = [...candidates];
  if (new Set(tags.map(tag => tag.split('-')[0])).size !== 1) return 'und';
  // A primary label can corroborate a region/script; conflicting specific tags cannot.
  const specific = tags.sort((a, b) => b.length - a.length)[0];
  return specific && tags.every(tag => specific === tag || specific.startsWith(`${tag}-`)) ? specific : 'und';
}

export interface TrackLanguageEvidence {
  /** MediaTrack-compatible field; language takes precedence when supplied. */
  lang?: string | null;
  language?: string | null;
  bcp47?: string | null;
  title?: string | null;
  /** Container specification default, used only when both fields are absent. */
  defaultLanguage?: string;
}

export function resolveTrackLanguage(evidence: TrackLanguageEvidence): string {
  const language = evidence.language !== undefined ? evidence.language : evidence.lang;
  for (const value of [evidence.bcp47, language]) {
    const lang = normalizeTrackLanguage(value);
    if (lang !== 'und') return lang;
  }
  const inferred = titleLanguage(evidence.title);
  if (inferred !== 'und') return inferred;
  if (language == null && evidence.bcp47 == null) {
    return normalizeTrackLanguage(evidence.defaultLanguage);
  }
  return 'und';
}
